import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, CheckCircle2, XCircle, FileText, Download, Building2, Mail, Phone, MapPin, User, Calendar, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { API, formatPrice, formatDate } from './utils';

/**
 * Detailed review modal for a SEPA request.
 * Shows: client info, pack, mandate, RC Pro, signed contract — and provides
 * single-click Approve / Reject actions.
 */
const SEPARequestDetailModal = ({ requestId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sepa-requests/${requestId}/details`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de charger le dossier');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetails(); }, [requestId]);

  const approve = async () => {
    if (!window.confirm(
      `Approuver cette demande SEPA ?\n\nUn premier prélèvement de ${formatPrice(data.request.first_charge_amount)} (caution + 1er loyer) sera lancé immédiatement.`
    )) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/sepa-requests/${requestId}/approve`, {}, { withCredentials: true });
      toast.success('Demande approuvée. Souscription Stripe créée.');
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'approbation");
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async () => {
    if (rejectReason.trim().length < 5) {
      toast.error('Motif obligatoire (5 caractères min)');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/sepa-requests/${requestId}/reject`,
        { reason: rejectReason.trim() }, { withCredentials: true });
      toast.success('Demande refusée. Email envoyé au client.');
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du refus');
    } finally {
      setBusy(false);
    }
  };

  const openSecureFile = async (path) => {
    // Use axios to fetch the file via authenticated XHR (cookies), then open in new tab
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      // Revoke after a delay to allow the browser to load it
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Impossible d'ouvrir le fichier");
    }
  };

  if (loading || !data) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={onClose}>
        <div className="bg-slate-900 rounded-xl p-8" onClick={(e) => e.stopPropagation()}>
          <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
        </div>
      </div>
    );
  }

  const r = data.request;
  const mo = data.manual_order;
  const res = data.reservation;
  const rc = data.rc_pro;
  const contract = data.contract;
  const isPending = r.status === 'pending_approval';

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-slate-900 border border-yellow-400/30 rounded-2xl w-full max-w-2xl my-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="sepa-detail-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-400/5 px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-5 h-5 text-yellow-400" />
              <h2 className="text-lg font-bold text-white">Dossier SEPA — {r.customer_name}</h2>
            </div>
            <div className="text-xs text-slate-400">
              ID: <span className="font-mono">{r.id.slice(0, 8)}</span> · Créé le {formatDate(r.created_at)}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="sepa-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          {/* Status banner */}
          <div className={`mb-4 px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
            r.status === 'pending_approval' ? 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/30' :
            r.status === 'approved' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' :
            r.status === 'rejected' ? 'bg-red-500/15 text-red-300 border border-red-500/30' :
            'bg-slate-500/15 text-slate-300 border border-slate-500/30'
          }`}>
            <AlertCircle className="w-4 h-4" />
            <span>
              {r.status === 'pending_approval' && 'À valider'}
              {r.status === 'approved' && `Approuvée le ${formatDate(r.approved_at)} par ${r.approved_by}`}
              {r.status === 'rejected' && `Refusée le ${formatDate(r.rejected_at)} — ${r.rejection_reason}`}
              {r.status === 'awaiting_mandate' && 'En attente de signature du client'}
            </span>
          </div>

          {/* Customer info */}
          <div className="mb-5">
            <h3 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <User className="w-3.5 h-3.5" /> Client
            </h3>
            <div className="bg-slate-800/50 rounded-lg p-4 space-y-1.5 text-sm">
              <div className="flex items-center gap-2 text-white"><span className="text-slate-400 w-20 text-xs">Nom</span>{r.customer_name}</div>
              <div className="flex items-center gap-2 text-white"><Mail className="w-3 h-3 text-slate-400" /><a href={`mailto:${r.customer_email}`} className="text-yellow-300 hover:underline">{r.customer_email}</a></div>
              {mo?.phone && <div className="flex items-center gap-2 text-white"><Phone className="w-3 h-3 text-slate-400" /><a href={`tel:${mo.phone}`} className="text-yellow-300 hover:underline">{mo.phone}</a></div>}
              {mo?.company && <div className="flex items-center gap-2 text-white"><Building2 className="w-3 h-3 text-slate-400" />{mo.company}</div>}
              {(mo?.address || res?.delivery_address) && (
                <div className="flex items-start gap-2 text-slate-300"><MapPin className="w-3 h-3 text-slate-400 mt-1" />
                  <span className="text-xs leading-relaxed">{mo?.address || res?.delivery_address}, {mo?.postal_code || res?.delivery_postal_code} {mo?.city || res?.delivery_city}</span>
                </div>
              )}
            </div>
          </div>

          {/* Order details */}
          <div className="mb-5">
            <h3 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" /> Commande
            </h3>
            <div className="bg-slate-800/50 rounded-lg p-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="text-slate-400">Pack</div><div className="text-white text-right font-medium">{r.pack_name}</div>
              {mo?.quote_number && <><div className="text-slate-400">Devis</div><div className="text-white text-right font-mono text-xs">{mo.quote_number}</div></>}
              {mo?.commercial_name && <><div className="text-slate-400">Commercial</div><div className="text-white text-right">{mo.commercial_name}</div></>}
              {(mo?.rental_start_date || res?.rental_start_date) && (
                <>
                  <div className="text-slate-400">Date de location</div>
                  <div className="text-white text-right text-xs">
                    {mo?.rental_start_date || res?.rental_start_date}
                    {res?.rental_end_date && ` → ${res.rental_end_date}`}
                  </div>
                </>
              )}
              <div className="text-slate-400 col-span-2 border-t border-slate-700 mt-2 pt-2"></div>
              <div className="text-slate-400">Mensualité</div><div className="text-white text-right">{formatPrice(r.monthly_price)}</div>
              <div className="text-slate-400">Caution</div><div className="text-white text-right">{formatPrice(r.caution)}</div>
              {r.transport_fees > 0 && <><div className="text-slate-400">Transport</div><div className="text-white text-right">{formatPrice(r.transport_fees)}</div></>}
              {mo?.discount_amount > 0 && <><div className="text-emerald-400">Remise</div><div className="text-emerald-400 text-right">-{formatPrice(mo.discount_amount)}</div></>}
              <div className="text-slate-400 col-span-2 border-t border-slate-700 mt-2 pt-2"></div>
              <div className="text-yellow-400 font-semibold">1er prélèvement</div>
              <div className="text-yellow-400 font-bold text-right text-base">{formatPrice(r.first_charge_amount)}</div>
            </div>
            {mo?.notes && (
              <div className="mt-2 text-xs text-slate-400 italic bg-slate-800/30 px-3 py-2 rounded">Note commerciale : {mo.notes}</div>
            )}
          </div>

          {/* Mandate IBAN */}
          <div className="mb-5">
            <h3 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5" /> Mandat SEPA
            </h3>
            <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-400 mb-1">IBAN signé</div>
                  {r.iban_last4 ? (
                    <div className="text-white font-mono" data-testid="sepa-detail-iban">**** **** **** {r.iban_last4}</div>
                  ) : r.status === 'awaiting_mandate' ? (
                    <div className="text-amber-300/80 text-xs italic" data-testid="sepa-detail-iban-pending">En attente de signature du client</div>
                  ) : (
                    <div className="text-slate-500 font-mono" data-testid="sepa-detail-iban">**** **** **** ????</div>
                  )}
                </div>
                {r.mandate_rum && (
                  <div className="text-right">
                    <div className="text-xs text-slate-400 mb-1">RUM</div>
                    <div className="text-white font-mono text-xs" data-testid="sepa-detail-rum">{r.mandate_rum}</div>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {data.mandate_pdf?.available ? (
                  <button
                    onClick={() => openSecureFile(data.mandate_pdf.download_url)}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded bg-yellow-400/15 border border-yellow-400/40 text-yellow-300 hover:bg-yellow-400/25"
                    data-testid="sepa-detail-mandate-pdf"
                  >
                    <FileText className="w-3.5 h-3.5" /> Mandat signé (PDF)
                  </button>
                ) : (
                  <span className="text-xs text-slate-500 italic">
                    {r.status === 'awaiting_mandate' ? 'Le client n\'a pas encore signé son mandat' : 'Mandat signé indisponible'}
                  </span>
                )}
                {r.mandate_url && (
                  <a
                    href={r.mandate_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-slate-600 text-slate-300 hover:bg-slate-800"
                    data-testid="sepa-detail-mandate"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Mandat Stripe
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Documents: RC Pro + Contract */}
          <div className="mb-5">
            <h3 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" /> Documents du dossier
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {/* RC Pro */}
              <div className={`p-3 rounded-lg border ${rc.available ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-slate-800/40 border-slate-700'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-white">RC Pro</span>
                  {rc.available ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-slate-500" />}
                </div>
                {rc.available ? (
                  <>
                    <div className="text-[11px] text-slate-400 truncate mb-2" title={rc.filename}>{rc.filename || 'attestation_rc.pdf'}</div>
                    <button
                      onClick={() => openSecureFile(rc.download_url)}
                      className="w-full text-xs px-2 py-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 flex items-center justify-center gap-1"
                      data-testid="sepa-detail-rc-download"
                    >
                      <Download className="w-3 h-3" /> Télécharger
                    </button>
                  </>
                ) : (
                  <div className="text-[11px] text-slate-500 italic">Non fournie</div>
                )}
              </div>
              {/* Contrat signé */}
              <div className={`p-3 rounded-lg border ${contract.available ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-slate-800/40 border-slate-700'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-white">Contrat signé</span>
                  {contract.available ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-slate-500" />}
                </div>
                {contract.available ? (
                  <>
                    <div className="text-[11px] text-slate-400 mb-2">Contrat de location PDF</div>
                    <button
                      onClick={() => openSecureFile(contract.download_url)}
                      className="w-full text-xs px-2 py-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 flex items-center justify-center gap-1"
                      data-testid="sepa-detail-contract-download"
                    >
                      <Download className="w-3 h-3" /> Télécharger
                    </button>
                  </>
                ) : (
                  <div className="text-[11px] text-slate-500 italic">
                    {mo ? 'Sera généré au paiement' : 'Non disponible'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Reject form when admin clicked Reject */}
          {rejecting && isPending && (
            <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <label className="block text-xs font-semibold text-red-300 uppercase mb-2">Motif du refus (envoyé au client)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Ex: IBAN dans un pays non couvert, vérification d'identité requise…"
                className="w-full p-2 bg-slate-800 border border-slate-700 rounded text-white text-sm focus:border-red-400 outline-none"
                data-testid="sepa-detail-reject-reason"
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => { setRejecting(false); setRejectReason(''); }}
                  className="flex-1 text-xs px-3 py-2 rounded border border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Retour
                </button>
                <button
                  onClick={submitReject}
                  disabled={busy || rejectReason.trim().length < 5}
                  className="flex-1 text-xs px-3 py-2 rounded bg-red-600 hover:bg-red-500 text-white font-semibold disabled:opacity-40"
                  data-testid="sepa-detail-reject-confirm"
                >
                  {busy ? 'Refus...' : 'Confirmer le refus'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        {isPending && !rejecting && (
          <div className="px-6 py-4 bg-slate-900/80 border-t border-slate-700 flex gap-3">
            <button
              onClick={() => setRejecting(true)}
              disabled={busy}
              className="flex-1 py-3 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400 font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="sepa-detail-reject-btn"
            >
              <XCircle className="w-4 h-4" /> Refuser
            </button>
            <button
              onClick={approve}
              disabled={busy}
              className="flex-1 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold flex items-center justify-center gap-2 disabled:opacity-40 shadow-lg shadow-emerald-500/20"
              data-testid="sepa-detail-approve-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {busy ? 'Approbation...' : `Approuver — ${formatPrice(r.first_charge_amount)}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SEPARequestDetailModal;
